import {
  Commitment,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import {
  useVbsWorkOrderShowQuery,
  useVbsWorkOrderUpdateRepresentativeMutation,
  useVbsWorkOrderUpdateServiceAddressMutation,
  useVbsWorkOrderUpdateServiceContactMutation,
} from "@/services/backend/vbs/work-orders/api/work-order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import t from "@/lang/lang";
import { PricingCards } from "@/services/backend/vbs/pricing/components/pricing-cards";
import { CalculationComponent } from "@/routes/processes/[processId]/vbs/calculations/[calculationId]/calculation/calculation-route";
import { TreatmentComponent } from "@/services/backend/vbs/treatments/components/treatment-component";
import { Column } from "@/services/backend/vbs/treatments/lib/treatment-component-state";
import { CopyToFutureCommitmentsDialog } from "@/routes/customers/[customerId]/relationships/[relationshipId]/commitments/[commitmentId]/_components/copy-to-future-commitments-dialog";
import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import { ContactCardWithMutation } from "@/services/backend/contacts/components/contact-card";
import { RepresentativeCardWithMutation } from "@/services/backend/representatives/components/representative-card";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { WorkOrderExecutingBranchSelect } from "@/services/backend/vbs/work-orders/components/work-order-executing-branch-select";
import { WorkOrderTeamLeaderInput } from "@/services/backend/vbs/work-orders/components/work-order-team-leader-input";
import { WorkOrderInternalCommentInput } from "@/services/backend/vbs/work-orders/components/work-order-comment-input";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyNameForDocument } from "@/services/backend/cpy/lib/cpy-name-for-document";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";

export function VbsDocumentPage({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  const { data, isLoading, error } = useVbsWorkOrderShowQuery({
    id: commitment.documentId!,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} ignoreValidationError={false} />;
  }

  if (isLoading) {
    return <CardContentSkeleton />;
  }

  // The work order of the commitment is the document of this page.
  return (
    <ErrValidationFailedContextProvider>
      <div className="grid gap-2">
        <Tabs defaultValue="treatment">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="base-data">{t("Basisdaten")}</TabsTrigger>
              <TabsTrigger value="pricing">{t("Preise")}</TabsTrigger>
              <TabsTrigger value="calculation">{t("Kalkulation")}</TabsTrigger>
              <TabsTrigger value="treatment">{t("Behandlung")}</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <CpyDialog
                destinationEntityName={CpyNameForDocument(
                  GSMServiceKind.VBS,
                  commitment.documentKind!,
                )}
                destinationId={commitment.documentId!}
              />
              <CopyToFutureCommitmentsDialog
                relationship={relationship}
                commitment={commitment}
              />
            </div>
          </div>
          <TabsContent value="base-data">
            <BaseData workOrder={data!} />
          </TabsContent>
          <TabsContent value="pricing">
            <PricingCards pricingId={data!.pricingId} />
          </TabsContent>
          <TabsContent value="calculation">
            <CalculationComponent
              calculationId={data!.calculationId}
              defaultOpen={false}
            />
          </TabsContent>
          <TabsContent value="treatment">
            <TreatmentComponent
              treatmentId={data!.treatmentId}
              defaultColumns={[
                Column.Position,
                Column.ComponentKind,
                Column.ComponentData,
                Column.CleanTime,
                Column.Comment,
                Column.Actions,
              ]}
            />
          </TabsContent>
        </Tabs>
      </div>
    </ErrValidationFailedContextProvider>
  );
}

function BaseData({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <div className="grid gap-2">
      <RelationshipBaseData workOrder={workOrder!} />
      <ServiceAddressCardWithMutation
        id={workOrder!.id}
        address={workOrder!.serviceAddress}
        mutation={useVbsWorkOrderUpdateServiceAddressMutation}
      />
      <div className="md:grid md:grid-cols-2 md:gap-2">
        <ContactCardWithMutation
          fieldName="serviceContact"
          id={workOrder!.id}
          contact={workOrder!.serviceContact}
          mutation={useVbsWorkOrderUpdateServiceContactMutation}
        />
        <RepresentativeCardWithMutation
          id={workOrder!.id}
          representative={workOrder!.representative}
          mutation={useVbsWorkOrderUpdateRepresentativeMutation}
        />
      </div>
    </div>
  );
}

function RelationshipBaseData({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Arbeitsschein")}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <WorkOrderExecutingBranchSelect workOrder={workOrder} />
          <WorkOrderTeamLeaderInput workOrder={workOrder} />
        </div>
        <WorkOrderInternalCommentInput workOrder={workOrder} />
      </CardContent>
    </Card>
  );
}
